import React, { useState, useEffect } from 'react';
import { Layout, Menu, Dropdown, Avatar, Space } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { appAPI } from '../../services/api.ts';
import {
  DashboardOutlined, InboxOutlined, ShoppingCartOutlined,
  TruckOutlined, FileTextOutlined, UserOutlined, SettingOutlined,
  LogoutOutlined, DatabaseOutlined, BarChartOutlined, ToolOutlined,
  AppstoreOutlined
} from '@ant-design/icons';

const { Header, Sider, Content } = Layout;

interface MainLayoutProps {
  children: React.ReactNode;
}

// Mappa voce di menu → risorsa del sistema permessi (backend/permissions.py)
const MENU_RESOURCE_MAP: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/customer-quotes': 'customer-quotes',
  '/supplier-quotes': 'supplier-quotes',
  '/customer-orders': 'customer-orders',
  '/supplier-orders': 'supplier-orders',
  '/inventory': 'inventory',
  '/product-movements': 'product-movements',
  '/formazione': 'formazione-auto',
  '/customs-documents': 'customs-documents',
  '/shipping-destinations': 'shipping-destinations',
  '/unified-shipments': 'unified-shipments',
  '/container-types': 'container-types',
  '/international-shipment-statuses': 'international-shipment-statuses',
  '/report-giacenze': 'report-giacenze',
  '/report-situazione-cliente': 'report-situazione-cliente',
  '/customers': 'customers',
  '/suppliers': 'suppliers',
  '/importers': 'importers',
  '/products': 'products',
  '/warehouses': 'warehouses',
  '/couriers': 'couriers',
  '/utilita/controllo-quantita-oron': 'utilita-quantita-oron',
  '/utilita/controllo-udc': 'utilita-udc',
  '/utilita/controllo-fattura-corriere': 'utilita-fattura-corriere',
};

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [appVersion, setAppVersion] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, canRead } = useAuth();

  useEffect(() => {
    const fetchAppInfo = async () => {
      try {
        const appInfo = await appAPI.getAppInfo();
        setAppVersion(appInfo.app_version);
      } catch (error) {
        console.error('Errore nel caricamento versione:', error);
      }
    };
    fetchAppInfo();
  }, []);

  // Ordine dei gruppi = flusso operativo (preventivo → ordine → spedizione →
  // magazzino → report), poi le utilità e, in coda, la configurazione (anagrafiche).
  const baseMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'quotes',
      icon: <FileTextOutlined />,
      label: 'Preventivi',
      children: [
        { key: '/customer-quotes', label: 'Preventivi Cliente' },
        { key: '/supplier-quotes', label: 'Preventivi Fornitori' },
      ],
    },
    {
      key: 'orders',
      icon: <ShoppingCartOutlined />,
      label: 'Ordini',
      children: [
        { key: '/customer-orders', label: 'Ordini Cliente' },
        { key: '/supplier-orders', label: 'Ordini Fornitori' },
      ],
    },
    {
      // Flusso in entrata (fornitore → magazzino): prima la voce operativa, poi le tabelle di supporto
      key: 'shipping',
      icon: <TruckOutlined />,
      label: 'Spedizioni',
      children: [
        { key: '/unified-shipments', label: 'Gestione Spedizioni' },
        { key: '/container-types', label: 'Tipi Container' },
        { key: '/international-shipment-statuses', label: 'Stati Spedizione' },
      ],
    },
    {
      // Ingresso merce (documenti doganali) → giacenze → consultazione movimenti
      key: 'inventory',
      icon: <InboxOutlined />,
      label: 'Magazzino',
      children: [
        { key: '/inventory', label: 'Inventario' },
        { key: '/customs-documents', label: 'Documenti di Carico' },
        { key: '/product-movements', label: 'Storia Movimenti' },
        { key: '/formazione', label: 'Formazione' },
      ],
    },
    {
      key: 'reports',
      icon: <BarChartOutlined />,
      label: 'Report',
      children: [
        { key: '/report-giacenze', label: 'Report Giacenze' },
        { key: '/report-situazione-cliente', label: 'Situazione Cliente' },
      ],
    },
    {
      key: 'utilita',
      icon: <AppstoreOutlined />,
      label: 'Utilità',
      children: [
        { key: '/utilita/controllo-quantita-oron', label: 'Controllo Quantità con Oron' },
        { key: '/utilita/controllo-udc', label: 'Controllo UDC' },
        { key: '/utilita/controllo-fattura-corriere', label: 'Controllo Fattura Corriere' },
      ],
    },
    {
      // Controparti commerciali, poi prodotti, poi attori logistico-doganali.
      // Le Destinazioni sono le mete di consegna del CLIENTE (nave/hotel) usate
      // dagli ordini cliente: non riguardano le spedizioni in entrata.
      key: 'master-data',
      icon: <DatabaseOutlined />,
      label: 'Anagrafica',
      children: [
        { key: '/customers', label: 'Clienti' },
        { key: '/suppliers', label: 'Fornitori' },
        { key: '/products', label: 'Prodotti' },
        { key: '/warehouses', label: 'Magazzini' },
        { key: '/importers', label: 'Importatori' },
        { key: '/couriers', label: 'Corrieri' },
        { key: '/shipping-destinations', label: 'Destinazioni Cliente' },
      ],
    },
  ];

  // Filtra le voci in base ai permessi del livello utente ('none' = nascosta);
  // i gruppi rimasti senza figli spariscono. L'admin vede sempre tutto.
  const visibleMenuItems = baseMenuItems
    .map((item: any) => {
      if (!item.children) {
        const resource = MENU_RESOURCE_MAP[item.key];
        return !resource || canRead(resource) ? item : null;
      }
      const children = item.children.filter((child: any) => {
        const resource = MENU_RESOURCE_MAP[child.key];
        return !resource || canRead(resource);
      });
      return children.length > 0 ? { ...item, children } : null;
    })
    .filter(Boolean) as typeof baseMenuItems;

  // Gruppi riservati all'Amministratore (level_id=1): fuori dalla matrice permessi
  const menuItems = user?.level_id === 1 ? [
    ...visibleMenuItems,
    {
      key: 'admin-settings',
      icon: <SettingOutlined />,
      label: '⚙️ Impostazioni',
      style: { borderTop: '1px solid #434343', marginTop: '8px' },
      children: [
        { key: '/settings/api', label: 'API' },
        { key: '/settings/permissions', label: 'Permessi' },
        { key: '/settings/aliases', label: 'Corrispondenze' },
        // Gestione utenti: le scritture sono già solo-admin lato API (require_admin)
        { key: '/users', label: 'Utenti' },
      ],
    },
    {
      key: 'admin-tools',
      icon: <ToolOutlined />,
      label: '🔧 Strumenti',
      style: { color: '#ff7875' },
      children: [
        { key: '/dev-tools', label: 'Dev Tools' },
        { key: '/tools/missing-customer-associations', label: 'Associazioni Cliente Mancanti' },
        { key: '/tools/stock-without-customer', label: 'Prodotti Stock Senza Cliente' },
        { key: '/tools/rollback-evasione-ordine', label: 'Rollback Evasione Ordine Cliente' },
        { key: '/tools/backup-db', label: 'Backup Database' },
        { key: '/tools/login-attempts', label: 'Tentativi di Accesso' },
        { key: '/formazione-tmp', label: 'Formazione-Tmp' },
      ],
    },
  ] : visibleMenuItems;

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profilo',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: logout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={250}
        style={{
          background: '#001529',
        }}
      >
        <div style={{
          height: 64,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
        }}>
          <span style={{ fontSize: collapsed ? '16px' : '20px' }}>
            {collapsed ? 'N' : 'NAVI'}
          </span>
          {!collapsed && appVersion && (
            <span style={{ fontSize: '15px', color: '#d9d9d9', fontWeight: 'normal', lineHeight: 1.2 }}>
              v{appVersion}
            </span>
          )}
        </div>
        <Menu
          theme="dark"
          selectedKeys={[location.pathname]}
          mode="inline"
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header style={{
          padding: '0 24px',
          background: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 1px 4px rgba(0,21,41,.08)',
        }}>
          <div />
          <Space>
            <span>Benvenuto, {user?.username}</span>
            <Dropdown
              menu={{ items: userMenuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Avatar
                style={{ cursor: 'pointer', backgroundColor: '#1890ff' }}
                icon={<UserOutlined />}
              />
            </Dropdown>
          </Space>
        </Header>
        <Content style={{
          margin: '24px',
          padding: '24px',
          background: '#fff',
          borderRadius: '6px',
          minHeight: 'calc(100vh - 112px)',
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;